import mitt, {type Emitter} from 'mitt';

export type User = {
    accessToken: string;
    expiresIn: number;
    displayName: string;
};

type AuthEvents = {
    auth: undefined;
    expire: undefined;
};

class AuthManager {
    private user: User | null = null;
    private expiresAt: number | null = null;
    private readonly emitter: Emitter<AuthEvents> = mitt();
    private timeout: number | null = null;

    public setUser(user: User) {
        this.user = user;
        this.expiresAt = Date.now() + user.expiresIn * 1000;
        this.emitter.emit('auth');

        if (this.timeout) {
            window.clearTimeout(this.timeout);
        }

        this.timeout = window.setTimeout(() => {
            this.clearUser();
        }, user.expiresIn * 1000);
    }

    public clearUser() {
        this.user = null;
        this.expiresAt = null;
        this.emitter.emit('expire');

        if (this.timeout) {
            window.clearTimeout(this.timeout);
        }
    }

    public hasTokenLifetimeLeft(time: number) {
        if (!this.expiresAt) {
            return false;
        }

        return this.expiresAt - time * 1000 < Date.now();
    }

    public getAccessToken() {
        if (!(this.user && this.expiresAt)) {
            return null;
        }

        if (this.expiresAt - 60 * 1000 < Date.now()) {
            return null;
        }

        return this.user.accessToken;
    }

    public addEventListener(event: keyof AuthEvents, handler: () => void) {
        this.emitter.on(event, handler);
    }

    public removeEventListener(event: keyof AuthEvents, handler: () => void) {
        this.emitter.off(event, handler);
    }
}

export const authManager = new AuthManager();
